import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { OptionFlowAlert, OptionFlowResponse } from '../types';
import { apiService } from '../services/apiService';
import { OptionFlowVisualization } from './OptionFlowVisualization';
import { TopMoversPanel } from './TopMoversPanel';

type FlowType = 'call-buy' | 'call-sell' | 'put-buy' | 'put-sell';
type SortField = 'ticker' | 'strike' | 'expiry' | 'totalPremium' | 'buyPremium' | 'sellPremium' | 'underlyingPrice' | 'volumeOiRatio' | 'tradeCount' | 'latestCreatedAt';
type SortDirection = 'asc' | 'desc';

interface GroupedFlow {
  ticker: string;
  strike: number;
  expiry: string;
  type: 'call' | 'put';
  totalPremium: number;
  buyPremium: number;
  sellPremium: number;
  totalSize: number;
  tradeCount: number;
  underlyingPrice: number;
  volumeOiRatio: number;
  flows: OptionFlowAlert[];
  latestCreatedAt: string;
}

export const UnusualFlowsComponent: React.FC = () => {
  const [allFlows, setAllFlows] = useState<OptionFlowAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FlowType>('call-buy');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isDateManuallySet, setIsDateManuallySet] = useState<boolean>(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Use refs to avoid stale closure in auto-refresh interval
  const selectedDateRef = useRef<string>('');
  const isDateManuallySetRef = useRef<boolean>(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);
  
  useEffect(() => {
    isDateManuallySetRef.current = isDateManuallySet;
  }, [isDateManuallySet]);
  
  const [premiumMin, setPremiumMin] = useState<string>('');
  const [premiumMax, setPremiumMax] = useState<string>('');
  const [showPremiumFilter, setShowPremiumFilter] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(50);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('totalPremium');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const premiumFilterButtonRef = useRef<HTMLButtonElement>(null);
  const premiumFilterDropdownRef = useRef<HTMLDivElement>(null);
  const [premiumFilterPosition, setPremiumFilterPosition] = useState<{top: number, left: number}>({top: 0, left: 0});

  const loadUnusualFlows = useCallback(async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      console.log('🔍 Loading unusual flows data...');
      console.log('🔧 API Config:', apiService.getApiConfig());
      
      // Fetch all option flow data
      const data: OptionFlowResponse = await apiService.getOptionFlow({ limit: 200 }, true);
      
      console.log('📊 Raw API data received:', {
        totalAlerts: data.data.length,
        fromCache: (data as any).from_cache,
        sampleAlert: data.data[0]
      });
      
      // Filter for unusual flows (>100K premium)
      const unusualFlows = data.data.filter(alert => {
        const premium = typeof alert.total_premium === 'string' ? parseFloat(alert.total_premium) : alert.total_premium;
        return premium >= 100000; // 100K filter
      });
      
      console.log('⚡ Filtered unusual flows (>100K):', unusualFlows.length);

      setAllFlows(unusualFlows);
      setLastRefreshTime(new Date());
      
      // Set default date to the most recent date (only on initial load, not when user has manually selected)
      if (!selectedDateRef.current && !isDateManuallySetRef.current && unusualFlows.length > 0) {
        const dates = Array.from(new Set(unusualFlows.map(flow => flow.created_at.split('T')[0])))
          .sort()
          .reverse();
        if (dates.length > 0) {
          const mostRecentDate = dates[0];
          console.log(`📅 Setting initial date to most recent: ${mostRecentDate}`);
          setSelectedDate(mostRecentDate);
        }
      } else if (isDateManuallySetRef.current) {
        console.log(`📅 Skipping auto-date because user manually selected: ${selectedDateRef.current}`);
      }
      
      console.log(`Unusual flows loaded: ${unusualFlows.length} flows`);
    } catch (err) {
      console.error('❌ Error loading unusual flows:', err);
      setError(`Failed to load unusual flows data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadUnusualFlows();
  }, [loadUnusualFlows]);

  useEffect(() => {
    // Auto-refresh every 30 seconds
    const interval = setInterval(async () => {
      console.log('Unusual Flows auto-refresh triggered at:', new Date().toLocaleTimeString());
      await loadUnusualFlows(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadUnusualFlows]);

  // Define filtering and grouping functions
  const getFilteredFlows = useCallback((flowType: FlowType): OptionFlowAlert[] => {
    const filteredFlows = allFlows.filter(flow => {
      // Filter by date first
      if (selectedDate && !flow.created_at.startsWith(selectedDate)) {
        return false;
      }

      // Filter by flow type
      const bidPrem = typeof flow.total_bid_side_prem === 'string' ? parseFloat(flow.total_bid_side_prem) : flow.total_bid_side_prem;
      const askPrem = typeof flow.total_ask_side_prem === 'string' ? parseFloat(flow.total_ask_side_prem) : flow.total_ask_side_prem;

      switch (flowType) {
        case 'call-buy':
          return flow.type === 'call' && askPrem > bidPrem;
        case 'call-sell':
          return flow.type === 'call' && bidPrem > askPrem;
        case 'put-buy':
          return flow.type === 'put' && askPrem > bidPrem;
        case 'put-sell':
          return flow.type === 'put' && bidPrem > askPrem;
        default:
          return false;
      }
    });
    
    return filteredFlows;
  }, [allFlows, selectedDate]);

  const groupFlowsByOptionChain = (flows: OptionFlowAlert[]): GroupedFlow[] => {
    const groups = new Map<string, GroupedFlow>();

    flows.forEach(flow => {
      const key = flow.option_chain || `${flow.ticker}-${flow.strike}-${flow.expiry}`;
      const bidPrem = typeof flow.total_bid_side_prem === 'string' ? parseFloat(flow.total_bid_side_prem) : flow.total_bid_side_prem;
      const askPrem = typeof flow.total_ask_side_prem === 'string' ? parseFloat(flow.total_ask_side_prem) : flow.total_ask_side_prem;
      const totalPrem = typeof flow.total_premium === 'string' ? parseFloat(flow.total_premium) : flow.total_premium;
      const underlyingPrice = typeof flow.underlying_price === 'string' ? parseFloat(flow.underlying_price) : flow.underlying_price;
      const volumeOiRatio = typeof flow.volume_oi_ratio === 'string' ? parseFloat(flow.volume_oi_ratio) : flow.volume_oi_ratio;

      if (groups.has(key)) {
        const group = groups.get(key)!;
        group.totalPremium += totalPrem;
        group.buyPremium += askPrem;
        group.sellPremium += bidPrem;
        group.totalSize += flow.total_size;
        group.tradeCount += flow.trade_count;
        group.flows.push(flow);
        
        if (flow.created_at > group.latestCreatedAt) {
          group.latestCreatedAt = flow.created_at;
        }
      } else {
        groups.set(key, {
          ticker: flow.ticker,
          strike: flow.strike,
          expiry: flow.expiry,
          type: flow.type,
          totalPremium: totalPrem,
          buyPremium: askPrem,
          sellPremium: bidPrem,
          totalSize: flow.total_size,
          tradeCount: flow.trade_count,
          underlyingPrice: underlyingPrice,
          volumeOiRatio: volumeOiRatio,
          flows: [flow],
          latestCreatedAt: flow.created_at
        });
      }
    });

    const filteredGroups = Array.from(groups.values()).filter(group => group.flows.length > 1);
    return filteredGroups.sort((a, b) => b.totalPremium - a.totalPremium);
  };

  const formatPremium = (premium: number) => {
    return `$${premium.toLocaleString()}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTabLabel = (tab: FlowType): string => {
    switch (tab) {
      case 'call-buy': return 'Call Buy';
      case 'call-sell': return 'Call Sell';
      case 'put-buy': return 'Put Buy';
      case 'put-sell': return 'Put Sell';
    }
  };

  const getTabColor = (tab: FlowType): string => {
    switch (tab) {
      case 'call-buy': return '#10b981';
      case 'call-sell': return '#ef4444';
      case 'put-buy': return '#10b981';
      case 'put-sell': return '#ef4444';
    }
  };

  // Memoize filtered flows for current tab and date
  const currentFlows = useMemo(() => {
    const flows = getFilteredFlows(activeTab);
    if (selectedDate) {
      return flows.filter(flow => flow.created_at.startsWith(selectedDate));
    }
    return flows;
  }, [activeTab, getFilteredFlows, selectedDate]);
  
  // Apply search filtering
  const filteredBySearch = useMemo(() => {
    let filtered = currentFlows;
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(alert => 
        alert.ticker?.toLowerCase().includes(searchLower) ||
        alert.strike?.toString().includes(searchLower) ||
        alert.expiry?.toLowerCase().includes(searchLower) ||
        alert.option_chain?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [currentFlows, searchTerm]);
  
  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Memoize grouped flows with sorting and premium filtering
  const groupedFlows = useMemo(() => {
    let flows = groupFlowsByOptionChain(filteredBySearch);
    
    if (premiumMin.trim() || premiumMax.trim()) {
      flows = flows.filter(group => {
        const minValue = premiumMin.trim() ? parseFloat(premiumMin) : 0;
        const maxValue = premiumMax.trim() ? parseFloat(premiumMax) : Infinity;
        return group.totalPremium >= minValue && group.totalPremium <= maxValue;
      });
    }
    
    return flows.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (sortField === 'latestCreatedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredBySearch, sortField, sortDirection, premiumMin, premiumMax]);
  
  // Get only flows that are part of grouped flows (for visualization)
  const flowsForVisualization = useMemo(() => {
    const groupedFlowIds = new Set<string>();
    groupedFlows.forEach(group => {
      group.flows.forEach(flow => {
        groupedFlowIds.add(flow.id);
      });
    });
    
    return filteredBySearch.filter(flow => groupedFlowIds.has(flow.id));
  }, [groupedFlows, filteredBySearch]);
  
  // Pagination
  const totalPages = Math.ceil(groupedFlows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFlows = groupedFlows.slice(startIndex, endIndex);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleClearSearch = () => {
    setSearchTerm('');
    setPremiumMin('');
    setPremiumMax('');
    setCurrentPage(1);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setIsDateManuallySet(true);
    setCurrentPage(1);
  };

  // Update dropdown position on scroll and handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPremiumFilter) {
        const target = event.target as Node;
        const isClickOnButton = premiumFilterButtonRef.current && premiumFilterButtonRef.current.contains(target);
        const isClickOnDropdown = premiumFilterDropdownRef.current && premiumFilterDropdownRef.current.contains(target);
        
        if (!isClickOnButton && !isClickOnDropdown) {
          setShowPremiumFilter(false);
        }
      }
    };

    if (showPremiumFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPremiumFilter]);

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Memoize tab counts
  const tabCounts = useMemo(() => {
    const getCountForTab = (flowType: FlowType) => {
      let flows = getFilteredFlows(flowType);
      
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        flows = flows.filter(alert => 
          alert.ticker?.toLowerCase().includes(searchLower) ||
          alert.strike?.toString().includes(searchLower) ||
          alert.expiry?.toLowerCase().includes(searchLower) ||
          alert.option_chain?.toLowerCase().includes(searchLower)
        );
      }
      
      const grouped = groupFlowsByOptionChain(flows);
      return grouped.length;
    };
    
    return {
      'call-buy': getCountForTab('call-buy'),
      'call-sell': getCountForTab('call-sell'),
      'put-buy': getCountForTab('put-buy'),
      'put-sell': getCountForTab('put-sell'),
    };
  }, [getFilteredFlows, searchTerm]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading unusual flows data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => loadUnusualFlows()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="component-container">
      <div className="component-header">
        <h2 className="component-title">🔍 Unusual Flows</h2>
        {lastRefreshTime && (
          <p className="last-refresh">
            Last updated: {lastRefreshTime.toLocaleTimeString()} (Auto-refresh every 30s)
          </p>
        )}
      </div>

      {/* Top Movers AI Analysis Panel - Pass selected date for date-specific analysis */}
      <TopMoversPanel selectedDate={selectedDate} />

      {/* Tabs */}
      <div className="unusual-flows-tabs">
        {(['call-buy', 'call-sell', 'put-buy', 'put-sell'] as FlowType[]).map(tab => (
          <button
            key={tab}
            className={`unusual-flows-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            style={{ 
              borderBottomColor: activeTab === tab ? getTabColor(tab) : 'transparent',
              color: activeTab === tab ? getTabColor(tab) : 'var(--text-secondary)'
            }}
          >
            {getTabLabel(tab)}
            <span className="tab-count">({tabCounts[tab]})</span>
          </button>
        ))}
      </div>

      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        <div className="search-container">
          <div className="search-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search ticker, strike, expiry, or option chain..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value.toUpperCase());
              setCurrentPage(1);
            }}
            className="search-input"
            style={{ textTransform: 'uppercase' }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="search-clear"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {searchTerm && (
          <button onClick={handleClearSearch} className="clear-filters-button">
            Clear Search
          </button>
        )}
      </div>

      {/* Content */}
      <div className="unusual-flows-content">
        {/* Option Flow Visualization */}
        <OptionFlowVisualization 
          alerts={flowsForVisualization}
          ticker={searchTerm.trim() || undefined}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          availableDates={Array.from(new Set(allFlows.map(flow => flow.created_at.split('T')[0])))}
        />
        
        <div className="unusual-flows-summary">
          <p>
            Showing <strong>{groupedFlows.length}</strong> grouped flows for <strong>{getTabLabel(activeTab)}</strong> 
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>

        {/* Top Pagination */}
        {groupedFlows.length > itemsPerPage && (
          <div className="pagination-container pagination-top">
            <div className="pagination-info">
              <span>
                Showing {startIndex + 1} to {Math.min(endIndex, groupedFlows.length)} of {groupedFlows.length} results
              </span>
            </div>
            
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>

              <div className="pagination-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-number ${
                        currentPage === pageNum
                          ? 'pagination-number-active'
                          : 'pagination-number-inactive'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="unusual-flows-table-container">
          <table className="unusual-flows-table">
            <thead className="table-header">
              <tr>
                <th className="sortable-header" onClick={() => handleSort('ticker')}>
                  <div className="table-header-content">
                    Ticker {getSortIcon('ticker')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('strike')}>
                  <div className="table-header-content">
                    Strike {getSortIcon('strike')}
                  </div>
                </th>
                <th>Type</th>
                <th className="sortable-header" onClick={() => handleSort('expiry')}>
                  <div className="table-header-content">
                    Expiry {getSortIcon('expiry')}
                  </div>
                </th>
                <th className="sortable-header">
                  <div className="table-header-content">
                    <span onClick={() => handleSort('totalPremium')} style={{ cursor: 'pointer' }}>
                      Total Prem {getSortIcon('totalPremium')}
                    </span>
                    <button
                      ref={premiumFilterButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (premiumFilterButtonRef.current) {
                          const rect = premiumFilterButtonRef.current.getBoundingClientRect();
                          const dropdownWidth = 300;
                          const viewportWidth = window.innerWidth;
                          
                          let left = rect.left;
                          if (left + dropdownWidth > viewportWidth) {
                            left = rect.right - dropdownWidth;
                          }
                          
                          setPremiumFilterPosition({
                            top: rect.bottom + 4,
                            left: Math.max(10, left)
                          });
                        }
                        setShowPremiumFilter(!showPremiumFilter);
                      }}
                      className="column-filter-button"
                      title="Filter by total premium range"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('buyPremium')}>
                  <div className="table-header-content">
                    Buy Prem {getSortIcon('buyPremium')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('sellPremium')}>
                  <div className="table-header-content">
                    Sell Prem {getSortIcon('sellPremium')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('latestCreatedAt')}>
                  <div className="table-header-content">
                    Created At {getSortIcon('latestCreatedAt')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('underlyingPrice')}>
                  <div className="table-header-content">
                    Underlying {getSortIcon('underlyingPrice')}
                  </div>
                </th>
                <th>Size</th>
                <th className="sortable-header" onClick={() => handleSort('volumeOiRatio')}>
                  <div className="table-header-content">
                    V/OI Ratio {getSortIcon('volumeOiRatio')}
                  </div>
                </th>
                <th className="sortable-header" onClick={() => handleSort('tradeCount')}>
                  <div className="table-header-content">
                    Trades {getSortIcon('tradeCount')}
                  </div>
                </th>
              </tr>
            </thead>
            
            {/* Premium Filter Dropdown */}
            {showPremiumFilter && (
              <div 
                ref={premiumFilterDropdownRef}
                className="column-filter-dropdown"
                style={{
                  position: 'fixed',
                  top: `${premiumFilterPosition.top}px`,
                  left: `${premiumFilterPosition.left}px`,
                  zIndex: 1000
                }}
              >
                <div className="column-filter-content">
                  <div className="column-filter-header">
                    <h4>Filter by Premium</h4>
                    <button
                      onClick={() => setShowPremiumFilter(false)}
                      className="column-filter-close"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="column-filter-inputs">
                    <div className="column-filter-input-group">
                      <label>Minimum Premium</label>
                      <input
                        type="number"
                        placeholder="Min ($)"
                        value={premiumMin}
                        onChange={(e) => {
                          setPremiumMin(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="column-filter-input"
                      />
                    </div>
                    <div className="column-filter-input-group">
                      <label>Maximum Premium</label>
                      <input
                        type="number"
                        placeholder="Max ($)"
                        value={premiumMax}
                        onChange={(e) => {
                          setPremiumMax(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="column-filter-input"
                      />
                    </div>
                  </div>
                  <div className="column-filter-actions">
                    <button
                      onClick={() => {
                        setPremiumMin('');
                        setPremiumMax('');
                        setCurrentPage(1);
                      }}
                      className="column-filter-clear"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowPremiumFilter(false)}
                      className="column-filter-apply"
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <tbody>
              {paginatedFlows.map((group) => {
                const groupId = `${group.ticker}-${group.strike}-${group.expiry}`;
                const isExpanded = expandedGroups.has(groupId);
                
                return (
                  <React.Fragment key={groupId}>
                    {/* Summary Row */}
                    <tr className="group-summary-row">
                      <td className="ticker-symbol" onClick={() => toggleGroupExpansion(groupId)} style={{ cursor: 'pointer' }}>
                        {isExpanded ? '▼' : '▶'} {group.ticker}
                      </td>
                      <td>${group.strike}</td>
                      <td>
                        <span className={`type-badge ${group.type}`}>
                          {group.type.toUpperCase()}
                        </span>
                      </td>
                      <td>{group.expiry}</td>
                      <td className="premium-value">{formatPremium(group.totalPremium)}</td>
                      <td className="premium-value">{formatPremium(group.buyPremium)}</td>
                      <td className="premium-value">{formatPremium(group.sellPremium)}</td>
                      <td>{formatDateTime(group.latestCreatedAt)}</td>
                      <td>${group.underlyingPrice.toFixed(2)}</td>
                      <td>{group.totalSize.toLocaleString()}</td>
                      <td>{group.volumeOiRatio.toFixed(2)}</td>
                      <td>{group.tradeCount}</td>
                    </tr>
                    
                    {/* Individual Trade Rows */}
                    {isExpanded && group.flows.map((flow, flowIndex) => (
                      <tr key={`${flow.id}-${flowIndex}`} className="individual-trade-row">
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="premium-value">{formatPremium(typeof flow.total_premium === 'string' ? parseFloat(flow.total_premium) : flow.total_premium)}</td>
                        <td className="premium-value">{formatPremium(typeof flow.total_ask_side_prem === 'string' ? parseFloat(flow.total_ask_side_prem) : flow.total_ask_side_prem)}</td>
                        <td className="premium-value">{formatPremium(typeof flow.total_bid_side_prem === 'string' ? parseFloat(flow.total_bid_side_prem) : flow.total_bid_side_prem)}</td>
                        <td>{formatDateTime(flow.created_at)}</td>
                        <td></td>
                        <td>{flow.total_size.toLocaleString()}</td>
                        <td>${typeof flow.price === 'string' ? parseFloat(flow.price).toFixed(2) : flow.price.toFixed(2)}</td>
                        <td>{flow.trade_count}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bottom Pagination */}
        {groupedFlows.length > itemsPerPage && (
          <div className="pagination-container pagination-bottom">
            <div className="pagination-info">
              <span>
                Showing {startIndex + 1} to {Math.min(endIndex, groupedFlows.length)} of {groupedFlows.length} results
              </span>
            </div>
            
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>

              <div className="pagination-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-number ${
                        currentPage === pageNum
                          ? 'pagination-number-active'
                          : 'pagination-number-inactive'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {groupedFlows.length === 0 && (
          <div className="empty-state">
            <p>No grouped flows found for the selected criteria.</p>
            {searchTerm || premiumMin || premiumMax ? (
              <button onClick={handleClearSearch} className="clear-filters-button">
                Clear Filters
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
